import {
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import add from 'date-fns/add';
import isValid from 'date-fns/isValid';
import parse from 'date-fns/parse';
import { DateTime } from 'luxon';
import { isNullOrEmpty } from '../../core/utils/common-functions';
import { DateRangeSetting } from '../../models/rt-common/date-range-setting';

export class ValidatorsPlus extends Validators {
  /**
   * Enable validator to be executed when callback function returns true.
   *
   * @static
   * @param {(ValidatorFn | null)} validator
   * @param {() => boolean} callback
   * @return {*}  {(ValidatorFn | null)}
   * @memberof ValidatorsPlus
   */
  static enableValidatorWhen(
    validator: ValidatorFn | any,
    callback: () => boolean
  ): ValidatorFn | null {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      if (!validator) {
        return null;
      }

      return callback() ? validator(ctrl) : null;
    };
  }

  /**
   *  Validator that requires the control's value to be less than (or equal) to the provided date.
   *
   * @static
   * @param {Date} max
   * @param {boolean} [orEqual=true]
   * @return {*}  {(ValidatorFn | null)}
   * @memberof ValidatorsPlus
   */
  static maxDate(max: Date, orEqual = true): ValidatorFn | null {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      if (!ctrl) {
        return null;
      }

      const maxDateTime = DateTime.fromJSDate(max);
      const targetDateTime = DateTime.fromJSDate(ctrl.value);

      if (!maxDateTime.isValid) {
        throw 'Invalid max date.';
      }

      if (!targetDateTime.isValid) {
        return null;
      }

      const maxDate = maxDateTime.startOf('day');
      const targetDate = targetDateTime.startOf('day');

      const result = (orEqual ? targetDate > maxDate : targetDate >= maxDate)
        ? { maxDate: { maxDate, orEqual } }
        : null;

      return result;
    };
  }

  /**
   *  Validator that requires the control's value to be greater than (or equal) to the provided date.
   *
   * @static
   * @param {Date} min
   * @param {boolean} [orEqual=true]
   * @return {*}  {(ValidatorFn | null)}
   * @memberof ValidatorsPlus
   */
  static minDate(min: Date, orEqual = true, custMsg?: string): ValidatorFn {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      if (!ctrl) {
        return null;
      }

      const minDateTime = DateTime.fromJSDate(min);
      const targetDateTime = DateTime.fromJSDate(ctrl.value);

      if (!minDateTime.isValid) {
        if (!isNullOrEmpty(custMsg)) throw custMsg;
        throw 'Invalid min date.';
      }

      if (!targetDateTime.isValid) {
        return null;
      }

      const minDate = minDateTime.startOf('day');
      const targetDate = targetDateTime.startOf('day');

      const result = (orEqual ? targetDate < minDate : targetDate <= minDate)
        ? {
            minDate: {
              minDate: minDate,
              orEqual,
              custMsg: custMsg,
              replaceMap: {},
            },
          }
        : null;

      return result;
    };
  }
  /**
   *  Validator that requires the control's value to be greater than (or equal) to the provided date.
   *
   * @static
   * @param {minDateObj} 傳入pageData
   * @param {boolean} [orEqual=true]
   * @return {*}  {(ValidatorFn | null)}
   * @memberof ValidatorsPlus
   */
  static minDateByRef(
    minDateObj: any,
    minDateObjectKey: string,
    orEqual = true,
    custMsg?: string | Function
  ): ValidatorFn {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      if (!ctrl) {
        return null;
      }

      const minDateTime = DateTime.fromJSDate(minDateObj?.[minDateObjectKey]);
      const targetDateTime = DateTime.fromJSDate(ctrl.value);

      if (!minDateTime.isValid) {
        if (!isNullOrEmpty(custMsg)) throw custMsg;
        throw 'Invalid min date.';
      }

      if (!targetDateTime.isValid) {
        return null;
      }
      const useMsg = () => {
        try {
          if (custMsg instanceof Function) {
            return custMsg();
          }
        } catch (error) {
          custMsg;
        }
      };
      const minDate = minDateTime.startOf('day');
      const targetDate = targetDateTime.startOf('day');

      const result = (orEqual ? targetDate < minDate : targetDate <= minDate)
        ? {
            minDate: {
              minDate: minDate,
              orEqual,
              custMsg: useMsg(),
              replaceMap: {},
            },
          }
        : null;

      return result;
    };
  }
  /**
   * 日期區間大於(等於)檢核
   *
   * @static
   * @param {Date} minDate
   * @param {boolean} [orEqual=false]
   * @return {*}  {(ValidatorFn | null)}
   * @memberof DateRangeValidators
   */
  static dateRangeGreaterThan(
    minDate: Date,
    orEqual = false
  ): ValidatorFn | null {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      if (isNullOrEmpty(ctrl.value)) {
        return null;
      }
      const start = isNullOrEmpty(ctrl.value?.start)
        ? null
        : DateTime.fromJSDate(ctrl.value.start).set({
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0,
          });
      const end = isNullOrEmpty(ctrl.value?.end)
        ? null
        : DateTime.fromJSDate(ctrl.value.end).set({
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0,
          });
      const targetDate = DateTime.fromJSDate(minDate);
      const errorObj: any = {};
      if (isNullOrEmpty(start) === false) {
        if (orEqual) {
          if (start! < targetDate) {
            errorObj.startDateGreaterThanOrEqual = { minDate };
          }
        } else {
          if (start! <= targetDate) {
            errorObj.startDateGreaterThan = { minDate };
          }
        }
      }
      if (isNullOrEmpty(end) === false) {
        if (orEqual) {
          if (end! < targetDate) {
            errorObj.endDateGreaterThanOrEqual = { minDate };
          }
        } else {
          if (end! <= targetDate) {
            errorObj.endDateGreaterThan = { minDate };
          }
        }
      }
      return Object.keys(errorObj).length > 0 ? errorObj : null;
    };
  }

  /**
   * 日期區間起迄日檢核
   *
   * @static
   * @param {AbstractControl} ctrl
   * @return {*}  {(ValidationErrors | null)}
   * @memberof DateRangeValidators
   */
  static dateRangeMinMaxCheck(ctrl: AbstractControl): ValidationErrors | null {
    const val = ctrl.value as { start?: Date; end?: Date };
    if (
      isNullOrEmpty(val?.start) === false &&
      isNullOrEmpty(val?.end) === false
    ) {
      if (val.start! > val.end!) {
        return { rangeInvalid: true };
      }
    }
    return null;
  }

  /**
   * 日期區間起迄日檢核(Submit)
   *
   * @static
   * @param {AbstractControl} ctrl
   * @return {*}  {(ValidationErrors | null)}
   * @memberof DateRangeValidators
   */
  static dateRangeMinMaxCheckOnSubmit(
    ctrl: AbstractControl
  ): ValidationErrors | null {
    const val = ctrl.value as { start?: Date; end?: Date };
    if (
      isNullOrEmpty(val?.start) === false &&
      isNullOrEmpty(val?.end) === false
    ) {
      if (val.start! > val.end!) {
        return { rangeInvalid: true };
      }
    }
    return null;
  }

  /**
   * 日期區間必填檢核
   *
   * @static
   * @param {AbstractControl} ctrl
   * @return {*}  {(ValidationErrors | null)}
   * @memberof DateRangeValidators
   */
  static dateRangeRequired(ctrl: AbstractControl): ValidationErrors | null {
    const val = ctrl.value as { start?: Date; end?: Date };
    if (isNullOrEmpty(val?.start) && isNullOrEmpty(val?.end)) {
      // 起迄日皆為空
      return { startRequired: true, endRequired: true, dateRequired: true };
    } else if (isNullOrEmpty(val?.start) && isNullOrEmpty(val?.end) === false) {
      // 起日為空
      return { startRequired: true };
    } else if (isNullOrEmpty(val?.start) === false && isNullOrEmpty(val?.end)) {
      // 迄日為空
      return { endRequired: true };
    } else {
      return null;
    }
  }

  /**
   * 日期區間必填檢核(Submit)
   *
   * @static
   * @param {AbstractControl} control
   * @return {*}  {(ValidationErrors | null)}
   * @memberof DateRangeValidators
   */
  static dateRangeRequiredOnSubmit(
    control: AbstractControl
  ): ValidationErrors | null {
    if (isNullOrEmpty(control)) return null;
    return ValidatorsPlus.dateRangeRequired(control);
  }
  /**
   * 日期區間格式檢核
   *
   * @static
   * @param {AbstractControl} ctrl
   * @return {*}  {(ValidationErrors | null)}
   * @memberof DateRangeValidators
   */
  static dateRangeValidFormat(ctrl: AbstractControl): ValidationErrors | null {
    function checkIsValidDate(date?: Date): boolean {
      if (isNullOrEmpty(date)) return true;
      const datetime = DateTime.fromJSDate(date!);
      return datetime.isValid;
    }

    const val = ctrl.value as { start?: Date; end?: Date };
    const errObj = {} as any;
    if (checkIsValidDate(val?.start) === false) {
      errObj.startInvalidDate = true;
    }
    if (checkIsValidDate(val?.end) === false) {
      errObj.endInvalidDate = true;
    }

    return Object.keys(errObj).length === 0 ? null : errObj;
  }
  /**
   * 依照設定日期區間最大值設定區間檢核
   *
   * @static
   * @param rangeSetting 區間設定參數 DateRangeSetting
   * @returns
   */
  static dateRangeValidateWithSetting(
    rangeSetting: DateRangeSetting
  ): ValidatorFn | null {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      if (isNullOrEmpty(ctrl.value)) {
        return null;
      }
      const checker = ValidatorsPlus.dateRangeChecker(
        ctrl.value?.start,
        ctrl.value?.end,
        rangeSetting
      );
      let errorObj: any = {};
      if (!checker) {
        errorObj = {};
        switch (rangeSetting.unit) {
          case 'years':
            errorObj.exceedMaxDateRange = { range: rangeSetting.amount };
            break;
          default:
            break;
        }
      }
      return Object.keys(errorObj).length > 0 ? errorObj : null;
    };
  }

  /**
   * 日期區間大於系統日檢核
   *
   * @static
   * @param {AbstractControl} ctrl
   * @return {*}  {(ValidationErrors | null)}
   * @memberof DateRangeValidators
   */
  static dateRangeGreaterThanSysDate(
    ctrl: AbstractControl
  ): ValidationErrors | null {
    function checkIsValidDate(date?: Date): boolean {
      if (isNullOrEmpty(date)) return true;
      const datetime = DateTime.fromJSDate(date!);
      return datetime.isValid;
    }

    const val = ctrl.value as { start?: Date; end?: Date };
    const errObj = {} as any;
    const sysDate = new Date();
    if (checkIsValidDate(val?.start) === true) {
      if (val?.start! > sysDate) {
        errObj.startDateGreaterThenSysdate = true;
      }
    }
    if (checkIsValidDate(val?.end) === true) {
      if (val?.end! > sysDate) {
        errObj.endDateGreaterThenSysdate = true;
      }
    }

    return Object.keys(errObj).length === 0 ? null : errObj;
  }

  /**
   * 日期區間小於系統日檢核
   *
   * @static
   * @param {AbstractControl} ctrl
   * @return {*}  {(ValidationErrors | null)}
   * @memberof DateRangeValidators
   */
  static dateRangeLessThanSysDate(
    ctrl: AbstractControl
  ): ValidationErrors | null {
    function checkIsValidDate(date?: Date): boolean {
      if (isNullOrEmpty(date)) return true;
      const datetime = DateTime.fromJSDate(date!);
      return datetime.isValid;
    }

    const val = ctrl.value as { start?: Date; end?: Date };
    const errObj = {} as any;
    const sysDate = new Date();
    sysDate.setHours(0, 0, 0, 0);
    if (checkIsValidDate(val?.start) === true) {
      if (!isNullOrEmpty(val?.start!) && val?.start! < sysDate) {
        errObj.dateLessThenSysdate = true;
      } else if (!isNullOrEmpty(val!) && val! < sysDate) {
        errObj.dateLessThenSysdate = true;
      }
    }
    if (checkIsValidDate(val?.end) === true) {
      if (!isNullOrEmpty(val?.end!) && val?.end! < sysDate) {
        errObj.dateLessThenSysdate = true;
      } else if (!isNullOrEmpty(val!) && val! < sysDate) {
        errObj.dateLessThenSysdate = true;
      }
    }

    return Object.keys(errObj).length === 0 ? null : errObj;
  }

  /**
   * 日期大於系統日檢核
   *
   * @static
   * @param {AbstractControl} ctrl
   * @return {*}  {(ValidationErrors | null)}
   * @memberof DateRangeValidators
   */
  static dateGreaterThanSysDate(
    ctrl: AbstractControl
  ): ValidationErrors | null {
    function checkIsValidDate(date?: Date): boolean {
      if (isNullOrEmpty(date)) return true;
      const datetime = DateTime.fromJSDate(date!);
      return datetime.isValid;
    }

    const val = ctrl.value;
    const errObj = {} as any;
    const sysDate = new Date();
    if (checkIsValidDate(val) === true) {
      if (val! > sysDate) {
        errObj.dateGreaterThenSysdate = true;
      }
    }

    return Object.keys(errObj).length === 0 ? null : errObj;
  }
  /**
   * 統一編號/顧客Id 檢核規則
   *
   * @static
   * @param identityTypeFormControl 顧客身份別formControl
   * @param birthdateFormControl 顧客出生日formControl
   * @param sexualFormControl 顧客性別formControl
   * @returns
   */
  static idChecker(
    identityTypeFormControl: AbstractControl,
    birthdateFormControl?: AbstractControl,
    sexualFormControl?: AbstractControl,
    fromDLW: boolean = false,
    isActivelyResetGenderAndBirthday = false
  ): ValidatorFn | null {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      const getIdCheckType = (
        identityType: 'C' | 'I' | null,
        countryId: string
      ): string | null => {
        // 依照國別及客戶類型設定檢查的 type
        const setting = {
          MY: {
            I: 'malaysia',
          },
        } as any;

        if (!identityType) {
          return null;
        }
        return setting[countryId]?.[identityType] ?? null;
      };
      let regString!: RegExp;
      let regResult: ValidationErrors | null = { idNotAllow: true };
      if (ctrl.value === null || ctrl.hasError('required')) {
        return null;
      } else {
        const idType = getIdCheckType(
          identityTypeFormControl?.getRawValue(),
          globalThis.sigv.appContext.envConfig.locale
        );
        switch (idType) {
          case 'malaysia':
            const year =
              parseInt(ctrl.value.substr(0, 2), 10) > 50
                ? `19${ctrl.value.substr(0, 2)}`
                : `20${ctrl.value.substr(0, 2)}`;
            const monthDate = ctrl.value.substr(2, 4);
            const resultDate = year + monthDate;
            const useDate = parse(resultDate, 'yyyyMMdd', new Date());
            console.log(resultDate, useDate, ctrl.value, year, monthDate);
            regString = /^[0-9]{12,12}$/;
            regResult =
              regString.test(ctrl.value) && isValid(useDate) ? null : regResult;
            if (!regResult && fromDLW) {
              // 加入fromDLW判斷
              if (birthdateFormControl) {
                birthdateFormControl?.setValue(useDate);
              }
              if (sexualFormControl) {
                sexualFormControl?.setValue(
                  +ctrl.value.substr(11, 12) % 2 === 0 ? '2' : '1'
                );
              }
            } else if (!regResult && !fromDLW) {
              // 審查開放編輯生日 & 性別, 改在 idNo change 時處理, 檢核和設定預設值拆開
              if (!isActivelyResetGenderAndBirthday) {
              } else {
                //因應 #87191 連動統編更新性別及生日
                birthdateFormControl?.setValue(useDate);
                sexualFormControl?.setValue(
                  +ctrl.value.substr(11, 12) % 2 === 0 ? '2' : '1'
                );
              }
            }
            break;
          default:
            regResult = null;
            break;
        }
        return regResult;
      }
    };
  }
  /**
   * dealerCode 檢核(無邏輯，僅依傳入參數判斷是否顯示錯誤訊息)
   *
   * @static
   * @param dealerCodeNotAllow dealerCode 是否正確(true 表示不正確)
   * @returns
   */
  static dealerCodeChecker(
    dealerCodeNotAllow: AbstractControl
  ): ValidatorFn | null {
    return (): ValidationErrors | null => {
      let regResult: ValidationErrors | null = {
        dealerCodeNotAllow: true,
      };
      let notAllow = dealerCodeNotAllow.getRawValue();
      if (notAllow) {
        return regResult;
      } else {
        return {};
      }
    };
  }
  /**
   * 計算日期是否於區間
   * @param val
   * @returns
   */
  static dateRangeChecker = (
    start: Date,
    end: Date,
    rangeSetting: DateRangeSetting
  ) => {
    let checker = true;
    const useStart = isNullOrEmpty(start)
      ? null
      : DateTime.fromJSDate(start).set({
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0,
        });
    const useEnd = isNullOrEmpty(end)
      ? null
      : DateTime.fromJSDate(end).set({
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0,
        });
    const settingKey = rangeSetting.unit;
    const setting = {} as Duration;
    setting[settingKey] = rangeSetting.amount;
    if (useStart && useEnd) {
      const targetDate = add(useStart!.toJSDate(), setting);
      if (
        (!rangeSetting.isNegative && useEnd!.toJSDate() > targetDate) ||
        (rangeSetting.isNegative && useEnd!.toJSDate() < targetDate)
      ) {
        checker = false;
      }
    }
    return checker;
  };
  /**
   * 至少輸入兩個字
   * @param val
   * @returns
   */
  static enterAtLeastTwoCharacters(): ValidatorFn | null {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      let regResult: ValidationErrors | null = {
        enterAtLeastTwoCharacters: true,
      };
      if (isNullOrEmpty(ctrl.value)) {
        return null;
      }
      let value = ctrl.getRawValue();
      if (value.length === 1) {
        return regResult;
      } else {
        return {};
      }
    };
  }
}
